import {
  chaveDoDiaEmSaoPaulo,
  formatDateLong,
  formatDateTimeFull,
  FUSO_BRASILIA,
} from '@/shared/formatters';

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Agora mesmo';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `Há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `Há ${diffInWeeks} ${diffInWeeks === 1 ? 'semana' : 'semanas'}`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `Há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
  }

  return 'Há mais de um ano';
}

export const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return formatDateTimeFull(value) || '-';
};

export const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '-';
  return formatDateLong(dateStr) || '-';
};

const MS_POR_DIA = 86_400_000;

/**
 * Whole days elapsed between `iso` and now, never negative.
 * Returns `null` for a missing or unparseable date — formatting is the caller's.
 */
export const diasDesde = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const instante = new Date(iso).getTime();
  if (Number.isNaN(instante)) return null;
  return Math.max(0, Math.floor((Date.now() - instante) / MS_POR_DIA));
};

const diaCivilSpEmMs = (instante: Date): number | null => {
  const chave = chaveDoDiaEmSaoPaulo(instante);
  if (chave === null) return null;
  const [ano, mes, dia] = chave.split('-').map(Number);
  return Date.UTC(ano, mes - 1, dia);
};

const diaUtcEmMs = (iso: string): number | null => {
  const instante = new Date(iso.includes('T') ? iso : `${iso}T00:00:00Z`);
  if (Number.isNaN(instante.getTime())) return null;
  return Date.UTC(instante.getUTCFullYear(), instante.getUTCMonth(), instante.getUTCDate());
};

/**
 * Dias civis até uma data de prazo: `0` = vence hoje, negativo = vencida, `null` sem data.
 *
 * As duas pontas são lidas em fusos diferentes **de propósito**, e é isso que a torna
 * correta. A data alvo é uma DATA, não um instante — o backend grava vencimento à meia-noite
 * UTC —, então ela é lida pelo dia UTC: projetá-la em São Paulo a jogaria para as 21h do dia
 * anterior e todo prazo apareceria um dia adiantado ("vence hoje" saindo como "vencida"). Já
 * o "hoje" é o dia civil de São Paulo, porque às 22h SP o dia UTC já virou e um vencimento de
 * amanhã apareceria como hoje. Espelha `diffDiasAteDataSp` do core.
 */
export const diasAte = (
  iso: string | null | undefined,
  agora: Date = new Date(),
): number | null => {
  if (!iso) return null;
  const alvo = diaUtcEmMs(iso);
  const hoje = diaCivilSpEmMs(agora);
  if (alvo === null || hoje === null) return null;
  return Math.round((alvo - hoje) / MS_POR_DIA);
};

const CERTIFICADO_DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_BRASILIA,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * `08 ago. 2026, 14:30:05` — carimbo com segundos, em Brasília.
 *
 * Os segundos existem porque isto carimba os acessos do certificado, onde a ordem de
 * dois eventos do mesmo minuto é prova. O fuso é fixo pelo mesmo motivo: o certificado
 * não pode exibir uma hora que dependa de onde o leitor abriu a página.
 */
export const formatShortDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  const partes = CERTIFICADO_DATA_HORA.formatToParts(d);
  const parte = (tipo: Intl.DateTimeFormatPartTypes): string =>
    partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('day')} ${parte('month')} ${parte('year')}, ${parte('hour')}:${parte('minute')}:${parte('second')}`;
};

/** Hoje em `yyyy-mm-dd`, montado dos componentes LOCAIS — `toISOString()` devolveria UTC e viraria o dia à noite no Brasil. */
export const hojeISO = (): string => {
  const hoje = new Date();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  const dia = String(hoje.getDate()).padStart(2, '0');
  return `${hoje.getFullYear()}-${mes}-${dia}`;
};

/**
 * Regra de "data de início não pode ser no passado" na única forma que serve a criação E a
 * edição: a data só precisa ser hoje-ou-futura quando **muda**.
 *
 * Contrato que começou em 15/08 continua começando em 15/08 depois que 15/08 vira passado.
 * Exigir futuro na edição travava qualquer alteração de método de pagamento ou dia de
 * vencimento num contrato já vigente, sem que ninguém tivesse tocado na data — o campo nascia
 * inválido e o "Salvar Seção" não passava.
 *
 * Trocar para OUTRA data no passado continua barrado: a isenção é da data já persistida, não
 * do campo.
 *
 * @param dataISO valor atual do campo, `yyyy-mm-dd`
 * @param dataSalva valor persistido; `null` na criação
 * @example dataInicioPermitida('2026-08-15', '2026-08-15') // true — inalterada
 * @example dataInicioPermitida('2026-08-14', '2026-08-15') // false — mudou para o passado
 */
export const dataInicioPermitida = (dataISO: string, dataSalva?: string | null): boolean => {
  if (!dataISO) return true;
  if (dataSalva && dataISO === dataSalva) return true;
  // A comparação abaixo é LEXICOGRÁFICA: só é equivalente a comparar datas em `yyyy-mm-dd` bem
  // formado. O schema aceita qualquer string não vazia, e um valor como `9999-99-99` passaria
  // pelo `>=` sem ser data nenhuma. Formato inválido é rejeitado aqui, com a mensagem de data.
  if (!ehDataISOValida(dataISO)) return false;
  return dataISO >= hojeISO();
};

const FORMATO_DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

/** `yyyy-mm-dd` com dia que existe de fato — `2026-02-30` não passa. */
const ehDataISOValida = (dataISO: string): boolean => {
  if (!FORMATO_DATA_ISO.test(dataISO)) return false;
  const [ano, mes, dia] = dataISO.split('-').map(Number);
  const data = new Date(ano, mes - 1, dia);
  return data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;
};
